import { useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  MapPin,
  X,
  ArrowRight,
  Loader2,
  Wand2,
  Waves,
  Mountain,
  Landmark,
  Trees,
  Anchor,
  UtensilsCrossed,
  Compass,
  Droplets,
  PartyPopper,
  Users,
  Heart,
  Pyramid,
} from "lucide-react";

import {
  EXPLORA_DESTINATIONS,
  EXPLORA_CATEGORIES,
  countToursForDestination,
  findToursForDestination,
  type ExploraCategory,
  type ExploraDestination,
} from "@/lib/explora-destinations";
import {
  matchDestinationsByText,
  recommendDestinations,
} from "@/lib/explora-ai.functions";
import type { UnifiedTour } from "@/lib/catalog.functions";
import { UnifiedTourCard } from "@/components/site/UnifiedTourCard";
import { MexicoMap } from "./MexicoMap";

interface ExploraExperienceProps {
  tours: UnifiedTour[];
  lang: "es" | "en";
}

const CATEGORY_META: Record<
  ExploraCategory,
  { es: string; en: string; Icon: typeof Waves }
> = {
  playa: { es: "Playa", en: "Beach", Icon: Waves },
  cultura: { es: "Cultura", en: "Culture", Icon: Landmark },
  naturaleza: { es: "Naturaleza", en: "Nature", Icon: Trees },
  aventura: { es: "Aventura", en: "Adventure", Icon: Mountain },
  islas: { es: "Islas", en: "Islands", Icon: Anchor },
  arqueologia: { es: "Arqueología", en: "Archaeology", Icon: Compass },
  gastronomia: { es: "Gastronomía", en: "Food", Icon: UtensilsCrossed },
  cenotes: { es: "Cenotes", en: "Cenotes", Icon: Droplets },
  "cultura-maya": { es: "Cultura Maya", en: "Maya culture", Icon: Pyramid },
  "vida-nocturna": { es: "Vida nocturna", en: "Nightlife", Icon: PartyPopper },
  familiar: { es: "Familiar", en: "Family", Icon: Users },
  romance: { es: "Romance", en: "Romance", Icon: Heart },
};

type AiMode = "text" | "quiz";

export function ExploraExperience({ tours, lang }: ExploraExperienceProps) {
  const isEs = lang === "es";
  const [activeCategories, setActiveCategories] = useState<Set<ExploraCategory>>(new Set());
  const [selected, setSelected] = useState<ExploraDestination | null>(null);
  const [aiMode, setAiMode] = useState<AiMode>("text");
  const [aiHighlighted, setAiHighlighted] = useState<string[]>([]);
  const [aiReason, setAiReason] = useState<string>("");
  const [aiLoading, setAiLoading] = useState(false);
  const recommendationsRef = useRef<HTMLDivElement | null>(null);

  const [textQuery, setTextQuery] = useState("");
  const [quizVibe, setQuizVibe] = useState<Set<ExploraCategory>>(new Set());
  const [quizPace, setQuizPace] = useState<"tranquilo" | "balanceado" | "intenso">("balanceado");
  const [quizCompany, setQuizCompany] = useState<"solo" | "pareja" | "familia" | "amigos">("pareja");
  const [quizExtra, setQuizExtra] = useState("");

  const categoryLabel = (c: ExploraCategory) => (isEs ? CATEGORY_META[c].es : CATEGORY_META[c].en);

  const countBySlug = useMemo(() => {
    const out: Record<string, number> = {};
    for (const d of EXPLORA_DESTINATIONS) {
      out[d.slug] = countToursForDestination(d, tours);
    }
    return out;
  }, [tours]);

  const visibleSlugs = useMemo(() => {
    if (activeCategories.size === 0) {
      return new Set(EXPLORA_DESTINATIONS.map((d) => d.slug));
    }
    return new Set(
      EXPLORA_DESTINATIONS.filter((d) =>
        d.categories.some((c) => activeCategories.has(c)),
      ).map((d) => d.slug),
    );
  }, [activeCategories]);

  const highlightedSet = useMemo(() => new Set(aiHighlighted), [aiHighlighted]);

  const recommendedDestinations = useMemo(
    () =>
      aiHighlighted
        .map((slug) => EXPLORA_DESTINATIONS.find((d) => d.slug === slug))
        .filter((d): d is ExploraDestination => Boolean(d)),
    [aiHighlighted],
  );

  function toggleCategory(c: ExploraCategory) {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }

  function focusRecommendations() {
    setTimeout(() => {
      recommendationsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  async function runTextAi() {
    if (!textQuery.trim()) return;
    setAiLoading(true);
    setAiReason("");
    try {
      const res = await matchDestinationsByText({
        data: { query: textQuery.trim(), lang },
      });
      setAiHighlighted(res.slugs);
      setAiReason(
        res.reason ||
          (res.slugs.length > 0
            ? isEs
              ? "Estos destinos podrían encajar."
              : "These destinations could fit."
            : isEs
            ? "No encontramos coincidencias. Intenta otra descripción."
            : "No matches yet. Try another description."),
      );
      if (res.slugs.length > 0) focusRecommendations();
    } finally {
      setAiLoading(false);
    }
  }

  async function runQuiz() {
    setAiLoading(true);
    setAiReason("");
    try {
      const res = await recommendDestinations({
        data: {
          vibe: Array.from(quizVibe),
          pace: quizPace,
          company: quizCompany,
          extra: quizExtra || undefined,
          lang,
        },
      });
      setAiHighlighted(res.slugs);
      setAiReason(
        res.reason ||
          (res.slugs.length > 0
            ? isEs
              ? "Tu viaje a la medida."
              : "Your tailored trip."
            : isEs
            ? "No encontramos coincidencias claras."
            : "No clear matches yet."),
      );
      if (res.slugs.length > 0) focusRecommendations();
    } finally {
      setAiLoading(false);
    }
  }

  function clearAi() {
    setAiHighlighted([]);
    setAiReason("");
  }

  const examplePrompts = isEs
    ? [
        "Quiero playas tranquilas con cenotes",
        "Busco aventura en la naturaleza",
        "Algo romántico cerca del Caribe",
        "Quiero conocer la cultura maya",
      ]
    : [
        "Quiet beaches with cenotes",
        "Adventure in nature",
        "Something romantic in the Caribbean",
        "Maya culture experience",
      ];

  return (
    <div className="space-y-8">
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {EXPLORA_CATEGORIES.map((c) => {
              const meta = CATEGORY_META[c];
              const Icon = meta.Icon;
              const active = activeCategories.has(c);
              return (
                <button
                  key={c}
                  onClick={() => toggleCategory(c)}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all sm:text-sm",
                    active
                      ? "border-primary bg-primary text-primary-foreground shadow-sm"
                      : "border-border bg-background text-foreground/75 hover:border-primary/60 hover:text-foreground",
                  ].join(" ")}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {categoryLabel(c)}
                </button>
              );
            })}
            {activeCategories.size > 0 && (
              <button
                onClick={() => setActiveCategories(new Set())}
                className="ml-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                {isEs ? "Limpiar" : "Clear"}
              </button>
            )}
          </div>

          {/* Map */}
          <MexicoMap
            visibleSlugs={visibleSlugs}
            highlightedSlugs={highlightedSet}
            selectedSlug={selected?.slug ?? null}
            onSelect={(d) => setSelected(d)}
            countBySlug={countBySlug}
            categoryLabel={categoryLabel}
          />

          {/* AI reason banner */}
          <AnimatePresence>
            {aiReason && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm"
              >
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div className="flex-1 text-foreground/85">{aiReason}</div>
                <button
                  onClick={clearAi}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Cerrar"
                >
                  <X className="h-4 w-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {/* AI assistant */}
          <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-background p-4 shadow-[var(--shadow-card)]">
            <div className="mb-3 flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-primary" />
              <h3 className="font-display text-lg font-semibold">
                {isEs ? "Asistente de viaje IA" : "AI travel assistant"}
              </h3>
            </div>
            <div className="mb-3 flex gap-1 rounded-full bg-background p-1">
              {(["text", "quiz"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setAiMode(m)}
                  className={[
                    "flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                    aiMode === m
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                >
                  {m === "text"
                    ? isEs
                      ? "Texto libre"
                      : "Free text"
                    : isEs
                    ? "Cuestionario"
                    : "Quiz"}
                </button>
              ))}
            </div>

            {aiMode === "text" && (
              <div className="space-y-2">
                <textarea
                  value={textQuery}
                  onChange={(e) => setTextQuery(e.target.value)}
                  placeholder={
                    isEs
                      ? "Ej: Quiero playas tranquilas con cenotes y buena comida…"
                      : "E.g. I want quiet beaches with cenotes and great food…"
                  }
                  rows={3}
                  className="w-full resize-none rounded-lg border border-border bg-background p-2.5 text-sm outline-none focus:border-primary"
                />
                <div className="flex flex-wrap gap-1">
                  {examplePrompts.map((p) => (
                    <button
                      key={p}
                      onClick={() => setTextQuery(p)}
                      className="rounded-full border border-border bg-background px-2.5 py-0.5 text-[11px] text-muted-foreground hover:border-primary hover:text-foreground"
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button
                  onClick={runTextAi}
                  disabled={aiLoading || !textQuery.trim()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {isEs ? "Descubrir destinos" : "Discover destinations"}
                </button>
              </div>
            )}

            {aiMode === "quiz" && (
              <div className="space-y-3 text-sm">
                <div>
                  <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                    {isEs ? "Qué buscas" : "What you want"}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {EXPLORA_CATEGORIES.map((c) => {
                      const active = quizVibe.has(c);
                      return (
                        <button
                          key={c}
                          onClick={() =>
                            setQuizVibe((prev) => {
                              const n = new Set(prev);
                              if (n.has(c)) n.delete(c);
                              else n.add(c);
                              return n;
                            })
                          }
                          className={[
                            "rounded-full border px-2.5 py-1 text-[11px]",
                            active
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-background",
                          ].join(" ")}
                        >
                          {categoryLabel(c)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                    {isEs ? "Ritmo" : "Pace"}
                  </div>
                  <div className="flex gap-1.5">
                    {(["tranquilo", "balanceado", "intenso"] as const).map((p) => (
                      <button
                        key={p}
                        onClick={() => setQuizPace(p)}
                        className={[
                          "flex-1 rounded-full border px-2 py-1 text-xs capitalize",
                          quizPace === p
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border",
                        ].join(" ")}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                    {isEs ? "Con quién" : "Who with"}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(["solo", "pareja", "familia", "amigos"] as const).map((c) => (
                      <button
                        key={c}
                        onClick={() => setQuizCompany(c)}
                        className={[
                          "rounded-full border px-2.5 py-1 text-xs capitalize",
                          quizCompany === c
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border",
                        ].join(" ")}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <input
                  value={quizExtra}
                  onChange={(e) => setQuizExtra(e.target.value)}
                  placeholder={isEs ? "Algo más (opcional)" : "Anything else (optional)"}
                  className="w-full rounded-lg border border-border bg-background p-2 text-sm outline-none focus:border-primary"
                />
                <button
                  onClick={runQuiz}
                  disabled={aiLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {isEs ? "Recomendar" : "Recommend"}
                </button>
              </div>
            )}
          </div>

          {/* Destination drawer */}
          <AnimatePresence mode="wait">
            {selected ? (
              <motion.div
                key={selected.slug}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="overflow-hidden rounded-2xl border border-border bg-background shadow-sm"
              >
                <div className="relative h-40 w-full overflow-hidden">
                  <img loading="lazy" decoding="async" src={selected.image} alt={selected.name} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <button
                    onClick={() => setSelected(null)}
                    className="absolute right-2 top-2 rounded-full bg-background/80 p-1.5 text-foreground backdrop-blur transition-colors hover:bg-background"
                    aria-label="Cerrar"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-2 left-3 text-white">
                    <div className="text-xs opacity-85">{selected.state}</div>
                    <div className="font-display text-xl font-semibold">{selected.name}</div>
                  </div>
                </div>
                <div className="space-y-3 p-4">
                  <p className="text-sm text-foreground/80">
                    {isEs ? selected.description.es : selected.description.en}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selected.categories.map((c) => (
                      <span
                        key={c}
                        className="rounded-full bg-accent px-2 py-0.5 text-[11px] text-accent-foreground"
                      >
                        {categoryLabel(c)}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between border-t border-border pt-3">
                    <div className="text-sm">
                      <span className="font-semibold text-primary">
                        {countBySlug[selected.slug] ?? 0}
                      </span>
                      <span className="ml-1 text-muted-foreground">
                        {isEs ? "tours disponibles" : "tours available"}
                      </span>
                    </div>
                    <Link
                      to="/tours/$city"
                      params={{ city: selected.citySlug }}
                      className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
                    >
                      {isEs ? "Explorar" : "Explore"}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                <MapPin className="mx-auto mb-2 h-5 w-5 text-primary/60" />
                {isEs
                  ? "Toca un destino en el mapa para ver detalles."
                  : "Tap a destination on the map to see details."}
              </div>
            )}
          </AnimatePresence>
        </aside>
      </div>

      {/* AI Recommendations: destinations + real tours */}
      <div ref={recommendationsRef}>
        <AnimatePresence>
          {recommendedDestinations.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              <header className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                    <Sparkles className="h-3.5 w-3.5" />
                    {isEs ? "Recomendado por IA" : "AI recommended"}
                  </div>
                  <h2 className="mt-1 font-display text-2xl font-semibold sm:text-3xl">
                    {isEs ? "Destinos y tours para ti" : "Destinations and tours for you"}
                  </h2>
                </div>
                <button
                  onClick={clearAi}
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                >
                  {isEs ? "Limpiar" : "Clear"}
                </button>
              </header>

              {recommendedDestinations.map((dest) => {
                const destTours = findToursForDestination(dest, tours).slice(0, 4);
                return (
                  <div key={dest.slug} className="rounded-2xl border border-border bg-background p-4 shadow-[var(--shadow-card)] sm:p-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                      <div className="relative h-24 w-full overflow-hidden rounded-xl sm:h-20 sm:w-32">
                        <img loading="lazy" decoding="async" src={dest.image} alt={dest.name} className="h-full w-full object-cover" />
                      </div>
                      <div className="flex-1">
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{dest.state}</div>
                        <h3 className="font-display text-xl font-semibold">{dest.name}</h3>
                        <p className="mt-1 text-sm text-foreground/75">
                          {isEs ? dest.description.es : dest.description.en}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {dest.categories.slice(0, 4).map((c) => (
                            <span key={c} className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                              {categoryLabel(c)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <Link
                        to="/tours/$city"
                        params={{ city: dest.citySlug }}
                        className="inline-flex items-center gap-1 self-start rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
                      >
                        {isEs ? `Ver todo (${countBySlug[dest.slug] ?? 0})` : `View all (${countBySlug[dest.slug] ?? 0})`}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>

                    {destTours.length > 0 ? (
                      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {destTours.map((t) => (
                          <UnifiedTourCard key={t.id} tour={t} />
                        ))}
                      </div>
                    ) : (
                      <p className="mt-4 text-sm text-muted-foreground">
                        {isEs
                          ? "Aún no tenemos tours publicados para este destino — pronto se sumarán."
                          : "We don't have published tours for this destination yet — coming soon."}
                      </p>
                    )}
                  </div>
                );
              })}
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
